// DialerBotsTab.jsx - Modern Integrated Bots Tab for MonitoringV3
// Clean, corporate design matching the monitoring page aesthetic
// Version: 4.0.0 - Persistent streaming state for Live Logs

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Bot, RefreshCw, Play, Square, RotateCcw, BarChart3,
    Activity, Clock, WifiOff, ChevronLeft, ChevronRight,
    Phone, PhoneForwarded, Voicemail, PhoneOff, PhoneMissed,
    UserX, Ban, AlertTriangle, X, Check, Power, PowerOff,
    Mic, Radio, ChevronDown, Zap, Server, ToggleLeft, ToggleRight, AudioWaveform,
    CheckSquare, Settings, Volume2, Link2, AlertCircle, FileText, ArrowUpDown,
    Square as SquareIcon, RefreshCcw, FileWarning, Trash2, Terminal, Monitor
} from 'lucide-react'
import { monitoringApi, voiceSetApi, machineApi } from '../services/api'
import LinkVMModal from './LinkVMModal'
import CrashReportModal from './CrashReportModal'
import LiveLogsModal, { categorizeLog } from './LiveLogsModal'
import './DialerBotsTab.css'

const DATE_PRESETS = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7days', label: '7 Days' },
    { key: 'custom', label: 'Custom' }
]

const PAGE_SIZES = [25, 50, 100]

const formatTimeAgo = (date) => {
    if (!date) return '-'
    const diff = Math.abs(Math.floor((new Date() - new Date(date)) / 1000))
    if (diff < 60) return `${diff}s`
    if (diff < 3600) return `${Math.floor(diff / 60)}m`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`
    return `${Math.floor(diff / 86400)}d`
}

const getDateString = (date) => date.toISOString().split('T')[0]

// Toast
function Toast({ message, type, onClose }) {
    useEffect(() => {
        const timer = setTimeout(onClose, 4000)
        return () => clearTimeout(timer)
    }, [onClose])

    return (
        <div className={`dbt-toast dbt-toast-${type}`}>
            {type === 'success' ? <Check size={16} /> : <X size={16} />}
            <span>{message}</span>
            <button onClick={onClose}><X size={14} /></button>
        </div>
    )
}

// Confirm Dialog
function ConfirmDialog({ title, message, onConfirm, onCancel, checkboxes = [], confirmText = "Confirm" }) {
    const [checks, setChecks] = useState(checkboxes.map(cb => cb.checked ?? false))

    return (
        <div className="dbt-confirm-overlay" onClick={onCancel}>
            <div className="dbt-confirm-dialog" onClick={e => e.stopPropagation()}>
                <div className="dbt-confirm-title">{title}</div>
                <div className="dbt-confirm-message">{message}</div>
                {checkboxes.length > 0 && (
                    <div className="dbt-confirm-checkboxes">
                        {checkboxes.map((cb, i) => (
                            <label key={i} className="dbt-confirm-checkbox">
                                <input
                                    type="checkbox"
                                    checked={checks[i]}
                                    onChange={(e) => setChecks(prev => {
                                        const next = [...prev]
                                        next[i] = e.target.checked
                                        return next
                                    })}
                                />
                                <span>{cb.label}</span>
                            </label>
                        ))}
                    </div>
                )}
                <div className="dbt-confirm-actions">
                    <button className="dbt-confirm-no" onClick={onCancel}>Cancel</button>
                    <button className="dbt-confirm-yes" onClick={() => onConfirm(checks)}>{confirmText}</button>
                </div>
            </div>
        </div>
    )
}

export default function DialerBotsTab({ dialerId, dialerName, dateFrom, dateTo, onRefresh, onTabChange, onFilterSip }) {
    const navigate = useNavigate()
    const [bots, setBots] = useState([])
    const [loading, setLoading] = useState(true)
    const [commandState, setCommandState] = useState({})
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(50)
    const [selectedStatus, setSelectedStatus] = useState('')

    // Expanded VM rows
    const [expandedBot, setExpandedBot] = useState(null)

    // Modals
    const [voiceSetBot, setVoiceSetBot] = useState(null)
    const [transcriberBot, setTranscriberBot] = useState(null)
    const [linkVMBot, setLinkVMBot] = useState(null)
    const [crashReportBot, setCrashReportBot] = useState(null)
    const [liveLogsBot, setLiveLogsBot] = useState(null)
    const [deleteBotDialog, setDeleteBotDialog] = useState(null) // { type: 'single'|'bulk', bot?, bots? }

    // Bulk
    const [selectedBots, setSelectedBots] = useState(new Set())
    const [bulkLoading, setBulkLoading] = useState(false)
    const [showRangeModal, setShowRangeModal] = useState(false)
    const [rangeFrom, setRangeFrom] = useState('')
    const [rangeTo, setRangeTo] = useState('')

    // Bulk edit voice sets
    const [voiceSetBulkBots, setVoiceSetBulkBots] = useState([])
    const [voiceSetBulkMode, setVoiceSetBulkMode] = useState(false)

    // Bulk edit transcriber
    const [transcriberBulkBots, setTranscriberBulkBots] = useState([])
    const [transcriberBulkMode, setTranscriberBulkMode] = useState(false)

    // Toast & confirm
    const [toasts, setToasts] = useState([])
    const [confirmAction, setConfirmAction] = useState(null)

    // Dropdowns
    const [showVMDropdown, setShowVMDropdown] = useState(false)
    const [showBulkDropdown, setShowBulkDropdown] = useState(false)

    // Live Logs Streaming State (persists when modal is closed)
    // Map: sip_id -> { logs: [], connected: boolean, eventSource: EventSource }
    const [streamingSessions, setStreamingSessions] = useState({})
    const eventSourcesRef = useRef({}) // Keep EventSource refs outside state to avoid re-renders

    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now()
        setToasts(prev => [...prev, { id, message, type }])
    }, [])

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id))
    }, [])

    // ============================================
    // LIVE LOGS STREAMING MANAGEMENT
    // ============================================

    // Start streaming logs for a bot
    const startStreaming = useCallback((bot) => {
        const sipId = bot.sip_id
        if (eventSourcesRef.current[sipId]) {
            console.log(`[LiveLogs] Already streaming for ${sipId}`)
            return
        }

        const url = `https://dashboard.byredstone.com/api/monitoring/dev-logs/${sipId}/${bot.dialer_id}/subscribe`
        console.log(`[LiveLogs] Starting stream for ${sipId}:`, url)

        const eventSource = new EventSource(url)
        eventSourcesRef.current[sipId] = eventSource

        // Initialize session state
        setStreamingSessions(prev => ({
            ...prev,
            [sipId]: { logs: prev[sipId]?.logs || [], connected: false, isStreaming: true }
        }))

        eventSource.onopen = () => {
            console.log(`[LiveLogs] Connected for ${sipId}`)
            setStreamingSessions(prev => ({
                ...prev,
                [sipId]: { ...prev[sipId], connected: true }
            }))
        }

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data)
                if (data.connected) return

                if (data.logs && Array.isArray(data.logs)) {
                    setStreamingSessions(prev => {
                        const session = prev[sipId] || { logs: [], connected: true, isStreaming: true }
                        const newLogs = [...session.logs, ...data.logs.map(log => ({
                            ...log,
                            category: categorizeLog(log)
                        }))]
                        // Keep max 2000 logs
                        return {
                            ...prev,
                            [sipId]: {
                                ...session,
                                logs: newLogs.length > 2000 ? newLogs.slice(-2000) : newLogs
                            }
                        }
                    })
                }
            } catch (e) {
                console.error(`[LiveLogs] Parse error for ${sipId}:`, e)
            }
        }

        eventSource.onerror = () => {
            console.error(`[LiveLogs] Connection error for ${sipId}`)
            setStreamingSessions(prev => ({
                ...prev,
                [sipId]: { ...prev[sipId], connected: false }
            }))
        }
    }, [])

    // Stop streaming logs for a bot
    const stopStreaming = useCallback((sipId) => {
        const eventSource = eventSourcesRef.current[sipId]
        if (eventSource) {
            console.log(`[LiveLogs] Stopping stream for ${sipId}`)
            eventSource.close()
            delete eventSourcesRef.current[sipId]
        }
        setStreamingSessions(prev => ({
            ...prev,
            [sipId]: { ...prev[sipId], isStreaming: false, connected: false }
        }))
    }, [])

    // Toggle streaming for a bot
    const toggleStreaming = useCallback((bot) => {
        const sipId = bot.sip_id
        const session = streamingSessions[sipId]
        if (session?.isStreaming) {
            stopStreaming(sipId)
        } else {
            startStreaming(bot)
        }
    }, [streamingSessions, startStreaming, stopStreaming])

    // Clear logs for a bot
    const clearBotLogs = useCallback((sipId) => {
        setStreamingSessions(prev => ({
            ...prev,
            [sipId]: { ...prev[sipId], logs: [] }
        }))
    }, [])

    // Add a single log to a bot's session
    const addBotLog = useCallback((sipId, log) => {
        setStreamingSessions(prev => {
            const session = prev[sipId] || { logs: [], connected: false, isStreaming: false }
            return {
                ...prev,
                [sipId]: {
                    ...session,
                    logs: [...session.logs, { ...log, category: categorizeLog(log) }]
                }
            }
        })
    }, [])

    // Cleanup EventSources on unmount or dialer change
    useEffect(() => {
        return () => {
            Object.entries(eventSourcesRef.current).forEach(([sipId, es]) => {
                console.log(`[LiveLogs] Cleanup: closing stream for ${sipId}`)
                es.close()
            })
            eventSourcesRef.current = {}
        }
    }, [dialerId])

    // Date range from props
    const getDateRange = useCallback(() => {
        const today = new Date()
        return {
            from: dateFrom || getDateString(today),
            to: dateTo || getDateString(today)
        }
    }, [dateFrom, dateTo])

    // Fetch bots
    const fetchBots = useCallback(async (silent = false) => {
        if (!silent) setLoading(true)
        try {
            const botsRes = await monitoringApi.getBotsByDialer(dialerId)
            const botList = botsRes.data?.bots || []

            const dateRange = getDateRange()
            const sipStats = {}

            try {
                const statsRes = await monitoringApi.getStatsByBot({
                    dialer_id: dialerId,
                    from: dateRange.from,
                    to: dateRange.to
                })

                const botStats = statsRes.data?.bots || []
                for (const s of botStats) {
                    sipStats[s.sip_id] = {
                        total: s.total_calls || 0,
                        xfer: s.transfers || 0,
                        am: s.answering_machines || 0,
                        ni: s.not_interested || 0,
                        na: s.no_answer || 0,
                        dnc: s.dnc || 0,
                        dnq: s.dnq || 0
                    }
                }
            } catch (err) {
                console.warn('Failed to fetch stats:', err)
            }

            const enrichedBots = botList.map(bot => ({
                ...bot,
                calls: sipStats[bot.sip_id]?.total || 0,
                xfer: sipStats[bot.sip_id]?.xfer || 0,
                am: sipStats[bot.sip_id]?.am || 0,
                ni: sipStats[bot.sip_id]?.ni || 0,
                na: sipStats[bot.sip_id]?.na || 0,
                dnc: sipStats[bot.sip_id]?.dnc || 0,
                dnq: sipStats[bot.sip_id]?.dnq || 0
            }))

            setBots(enrichedBots)
        } catch (err) {
            console.error('Failed to load bots:', err)
            if (!silent) setBots([])
        } finally {
            if (!silent) setLoading(false)
        }
    }, [dialerId, getDateRange])

    useEffect(() => {
        fetchBots()
        const interval = setInterval(() => fetchBots(true), 10000)
        return () => clearInterval(interval)
    }, [fetchBots])

    // Stats
    const statusStats = useMemo(() => {
        const stats = { active: 0, dial_level_0: 0, waiting: 0, offline: 0 }
        bots.forEach(b => {
            const s = b.effective_status || 'offline'
            if (s === 'active' || s === 'running') stats.active++
            else if (s === 'dial_level_0') stats.dial_level_0++
            else if (s === 'waiting') stats.waiting++
            else stats.offline++
        })
        return stats
    }, [bots])

    const totals = useMemo(() => {
        return bots.reduce((acc, b) => ({
            calls: acc.calls + (b.calls || 0),
            xfer: acc.xfer + (b.xfer || 0),
            am: acc.am + (b.am || 0),
            ni: acc.ni + (b.ni || 0),
            na: acc.na + (b.na || 0),
            dnc: acc.dnc + (b.dnc || 0),
            dnq: acc.dnq + (b.dnq || 0)
        }), { calls: 0, xfer: 0, am: 0, ni: 0, na: 0, dnc: 0, dnq: 0 })
    }, [bots])

    // Auto stats
    const autoStats = useMemo(() => {
        return bots.reduce((acc, b) => ({
            autoLoginOn: acc.autoLoginOn + (b.auto_login ? 1 : 0),
            autoReviveOn: acc.autoReviveOn + (b.auto_revive ? 1 : 0)
        }), { autoLoginOn: 0, autoReviveOn: 0 })
    }, [bots])

    // Filter
    const filteredBots = useMemo(() => {
        if (!selectedStatus) return bots
        return bots.filter(b => {
            const s = b.effective_status || 'offline'
            return selectedStatus === 'active' ? (s === 'active' || s === 'running') : s === selectedStatus
        })
    }, [bots, selectedStatus])

    const totalPages = Math.ceil(filteredBots.length / pageSize)
    const paginatedBots = filteredBots.slice((currentPage - 1) * pageSize, currentPage * pageSize)

    useEffect(() => { setCurrentPage(1) }, [selectedStatus, dateFrom, dateTo])

    // Clear selection and command state when dialerId changes to prevent cross-dialer issues
    useEffect(() => {
        setSelectedBots(new Set())
        setCommandState({})
        setCurrentPage(1)
    }, [dialerId])

    // Commands
    const executeCommand = async (sipIds, command) => {
        setCommandState(prev => {
            const next = { ...prev }
            sipIds.forEach(sipId => {
                next[sipId] = { cmd: command, status: 'Sending...' }
            })
            return next
        })
        try {
            await monitoringApi.sendCommand(sipIds, command, dialerId)
            // Clear "Sending..." status after 15 seconds
            setTimeout(() => {
                setCommandState(prev => {
                    const next = { ...prev }
                    sipIds.forEach(sipId => {
                        if (next[sipId]?.status === 'Sending...') {
                            delete next[sipId]
                        }
                    })
                    return next
                })
            }, 15000)
        } catch (err) {
            showToast('Command failed', 'error')
            setCommandState(prev => {
                const n = { ...prev }
                sipIds.forEach(sipId => delete n[sipId])
                return n
            })
        }
    }

    const sendCommand = async (sipId, command) => {
        const bot = bots.find(b => b.sip_id === sipId)

        if (command === 'start' && bot) {
            const lastHB = bot.last_heartbeat ? new Date(bot.last_heartbeat) : null
            const hbAge = lastHB ? (Date.now() - lastHB) / 1000 : Infinity

            if (hbAge > 120) {
                setCommandState(prev => ({
                    ...prev,
                    [sipId]: { cmd: command, status: 'VM offline', error: true }
                }))
                showToast('Bot offline - start VM first', 'error')
                return
            }

            if (!bot.auto_login) {
                setConfirmAction({
                    title: 'Start Bot?',
                    message: `Enable auto-login for ${sipId}?`,
                    checkboxes: [
                        { label: 'Enable Auto-Login', checked: true },
                        { label: 'Enable Auto-Revive', checked: false }
                    ],
                    confirmText: 'Start',
                    onConfirm: async (checks) => {
                        setConfirmAction(null)
                        try {
                            if (checks[0]) await monitoringApi.setAutoLogin(sipId, true, dialerId)
                            if (checks[1]) await monitoringApi.setAutoRevive(sipId, true, dialerId)
                            setBots(prev => prev.map(b => b.sip_id === sipId ? {
                                ...b,
                                auto_login: checks[0] ? 1 : b.auto_login,
                                auto_revive: checks[1] ? 1 : b.auto_revive
                            } : b))
                        } catch (err) { }
                        executeCommand([sipId], command)
                    }
                })
                return
            }
        }

        if (command === 'stop' && bot && (bot.auto_login || bot.auto_revive)) {
            setConfirmAction({
                title: 'Stop Bot?',
                message: 'Also disable auto-login/revive?',
                checkboxes: [{ label: 'Disable Auto-Login/Revive', checked: false }],
                confirmText: 'Stop',
                onConfirm: async (checks) => {
                    setConfirmAction(null)
                    if (checks[0]) {
                        try {
                            await monitoringApi.setAutoLogin(sipId, false, dialerId)
                            await monitoringApi.setAutoRevive(sipId, false, dialerId)
                            setBots(prev => prev.map(b => b.sip_id === sipId ? { ...b, auto_login: 0, auto_revive: 0 } : b))
                        } catch (err) { }
                    }
                    executeCommand([sipId], command)
                }
            })
            return
        }

        executeCommand([sipId], command)
    }

    // VM actions
    const vmAction = async (action, bot) => {
        setConfirmAction({
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} VM?`,
            message: `${action} VM ${bot.vmid}?`,
            confirmText: action.charAt(0).toUpperCase() + action.slice(1),
            onConfirm: async () => {
                setConfirmAction(null)
                setCommandState(prev => ({ ...prev, [bot.sip_id]: { cmd: `vm_${action}`, status: `${action}ing...` } }))
                try {
                    if (action === 'start') await monitoringApi.vmStart(bot.server_ip, bot.vmid, bot.sip_id, dialerId)
                    else if (action === 'stop') await monitoringApi.vmStop(bot.server_ip, bot.vmid, bot.sip_id, dialerId)
                    else if (action === 'restart') await monitoringApi.vmRestart(bot.server_ip, bot.vmid, bot.sip_id, false, dialerId)
                    showToast(`VM ${action} sent`, 'success')
                    setTimeout(() => {
                        setCommandState(prev => { const n = { ...prev }; delete n[bot.sip_id]; return n })
                    }, 5000)
                } catch (err) {
                    showToast(`VM ${action} failed`, 'error')
                    setCommandState(prev => { const n = { ...prev }; delete n[bot.sip_id]; return n })
                }
            }
        })
    }

    // Toggle auto
    const toggleAutoLogin = async (bot) => {
        const newVal = !bot.auto_login
        setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_login: newVal ? 1 : 0 } : b))
        try {
            await monitoringApi.setAutoLogin(bot.sip_id, newVal, dialerId)
            showToast(`Auto-login ${newVal ? 'on' : 'off'}`, 'success')
        } catch (err) {
            setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_login: bot.auto_login } : b))
            showToast('Failed', 'error')
        }
    }

    const toggleAutoRevive = async (bot) => {
        if (!bot.auto_login) {
            showToast('Enable auto-login first', 'error')
            return
        }
        const newVal = !bot.auto_revive
        setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_revive: newVal ? 1 : 0 } : b))
        try {
            await monitoringApi.setAutoRevive(bot.sip_id, newVal, dialerId)
            showToast(`Auto-revive ${newVal ? 'on' : 'off'}`, 'success')
        } catch (err) {
            setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_revive: bot.auto_revive } : b))
            showToast('Failed', 'error')
        }
    }

    // Bulk Auto Actions
    const bulkAutoLogin = async (enable) => {
        const targetBots = selectedBots.size > 0
            ? bots.filter(b => selectedBots.has(b.sip_id))
            : filteredBots

        if (targetBots.length === 0) return

        setConfirmAction({
            title: `${enable ? 'Enable' : 'Disable'} Auto-Login`,
            message: `${enable ? 'Enable' : 'Disable'} auto-login for ${targetBots.length} bots?`,
            confirmText: enable ? 'Enable All' : 'Disable All',
            onConfirm: async () => {
                setConfirmAction(null)
                setBulkLoading(true)
                let success = 0
                for (const bot of targetBots) {
                    try {
                        await monitoringApi.setAutoLogin(bot.sip_id, enable, dialerId)
                        success++
                    } catch (err) { }
                }
                setBots(prev => prev.map(b =>
                    targetBots.some(t => t.sip_id === b.sip_id)
                        ? { ...b, auto_login: enable ? 1 : 0 }
                        : b
                ))
                showToast(`Auto-login ${enable ? 'enabled' : 'disabled'} for ${success} bots`, 'success')
                setBulkLoading(false)
                setSelectedBots(new Set())
            }
        })
    }

    const bulkAutoRevive = async (enable) => {
        const targetBots = selectedBots.size > 0
            ? bots.filter(b => selectedBots.has(b.sip_id))
            : filteredBots

        if (targetBots.length === 0) return

        setConfirmAction({
            title: `${enable ? 'Enable' : 'Disable'} Auto-Revive`,
            message: `${enable ? 'Enable' : 'Disable'} auto-revive for ${targetBots.length} bots?`,
            confirmText: enable ? 'Enable All' : 'Disable All',
            onConfirm: async () => {
                setConfirmAction(null)
                setBulkLoading(true)
                let success = 0
                for (const bot of targetBots) {
                    try {
                        if (enable && !bot.auto_login) {
                            await monitoringApi.setAutoLogin(bot.sip_id, true, dialerId)
                        }
                        await monitoringApi.setAutoRevive(bot.sip_id, enable, dialerId)
                        success++
                    } catch (err) { }
                }
                setBots(prev => prev.map(b =>
                    targetBots.some(t => t.sip_id === b.sip_id)
                        ? { ...b, auto_revive: enable ? 1 : 0, auto_login: enable ? 1 : b.auto_login }
                        : b
                ))
                showToast(`Auto-revive ${enable ? 'enabled' : 'disabled'} for ${success} bots`, 'success')
                setBulkLoading(false)
                setSelectedBots(new Set())
            }
        })
    }

    // Bulk
    const toggleSelectAll = () => {
        if (selectedBots.size === filteredBots.length) setSelectedBots(new Set())
        else setSelectedBots(new Set(filteredBots.map(b => b.sip_id)))
    }

    const toggleSelect = (sipId) => {
        setSelectedBots(prev => {
            const next = new Set(prev)
            if (next.has(sipId)) next.delete(sipId)
            else next.add(sipId)
            return next
        })
    }

    const selectRange = () => {
        // Extract prefix and number from inputs like "Agent1" or "8026"
        const parseId = (val) => {
            const match = String(val).match(/^([a-zA-Z]*)(\d+)$/)
            if (!match) return null
            return { prefix: match[1], num: parseInt(match[2]) }
        }
        const fromParsed = parseId(rangeFrom)
        const toParsed = parseId(rangeTo)
        if (!fromParsed || !toParsed || fromParsed.num > toParsed.num) {
            showToast('Invalid range. Use e.g. 8026-8030 or Agent1-Agent20', 'error')
            return
        }
        const prefix = fromParsed.prefix || toParsed.prefix || ''
        const matching = filteredBots.filter(b => {
            const parsed = parseId(b.sip_id)
            if (!parsed) return false
            // Match prefix (case insensitive) and number range
            if (prefix && parsed.prefix.toLowerCase() !== prefix.toLowerCase()) return false
            return parsed.num >= fromParsed.num && parsed.num <= toParsed.num
        })
        if (matching.length === 0) {
            showToast('No bots found in that range', 'error')
            return
        }
        setSelectedBots(prev => {
            const next = new Set(prev)
            matching.forEach(b => next.add(b.sip_id))
            return next
        })
        setShowRangeModal(false)
        setRangeFrom('')
        setRangeTo('')
        showToast(`Selected ${matching.length} bots (${rangeFrom}-${rangeTo})`)
    }

    // Bulk edit voice sets
    const openBulkVoiceSetModal = () => {
        if (selectedBots.size === 0) {
            showToast('Select bots first', 'error')
            return
        }
        const botsToEdit = bots.filter(b => selectedBots.has(b.sip_id) && b.license_id)
        if (botsToEdit.length === 0) {
            showToast('Selected bots have no licenses', 'error')
            return
        }
        setVoiceSetBulkBots(botsToEdit)
        setVoiceSetBulkMode(true)
        setVoiceSetBot(botsToEdit[0]) // Use first bot as reference
    }

    // Bulk edit transcriber
    const openBulkTranscriberModal = () => {
        if (selectedBots.size === 0) {
            showToast('Select bots first', 'error')
            return
        }
        const botsToEdit = bots.filter(b => selectedBots.has(b.sip_id) && b.license_id)
        if (botsToEdit.length === 0) {
            showToast('Selected bots have no licenses', 'error')
            return
        }
        setTranscriberBulkBots(botsToEdit)
        setTranscriberBulkMode(true)
        setTranscriberBot(botsToEdit[0]) // Use first bot as reference
    }

    const bulkCommand = async (command) => {
        if (selectedBots.size === 0) return
        const affectedSips = [...selectedBots]
        const affectedBots = bots.filter(b => selectedBots.has(b.sip_id))

        // For stop command, show confirmation with option to disable auto-login/revive
        if (command === 'stop') {
            const hasAutoSettings = affectedBots.some(b => b.auto_login || b.auto_revive)
            setConfirmAction({
                title: 'Logout Selected Bots',
                message: `Logout ${affectedSips.length} bots?`,
                checkboxes: hasAutoSettings ? [{ label: 'Disable Auto-Login/Revive', checked: false }] : [],
                confirmText: 'Logout',
                onConfirm: async (checks) => {
                    setConfirmAction(null)
                    setBulkLoading(true)
                    console.log(`[DialerBotsTab] bulkCommand: command=${command}, dialerId=${dialerId}, sips=${affectedSips.slice(0,5).join(',')}...`)
                    try {
                        // Disable auto-login/revive if checked
                        if (checks && checks[0]) {
                            for (const bot of affectedBots) {
                                if (bot.auto_login) await monitoringApi.setAutoLogin(bot.sip_id, false, dialerId)
                                if (bot.auto_revive) await monitoringApi.setAutoRevive(bot.sip_id, false, dialerId)
                            }
                            setBots(prev => prev.map(b =>
                                affectedSips.includes(b.sip_id) ? { ...b, auto_login: 0, auto_revive: 0 } : b
                            ))
                        }
                        await monitoringApi.sendCommand(affectedSips, command, dialerId)
                        setCommandState(prev => {
                            const next = { ...prev }
                            affectedSips.forEach(sipId => { next[sipId] = { cmd: command, status: 'Sending...' } })
                            return next
                        })
                        setSelectedBots(new Set())
                        showToast(`Logout command sent to ${affectedSips.length} bots`, 'success')
                        setTimeout(() => {
                            setCommandState(prev => {
                                const next = { ...prev }
                                affectedSips.forEach(sipId => {
                                    if (next[sipId]?.status === 'Sending...') delete next[sipId]
                                })
                                return next
                            })
                        }, 15000)
                    } catch (err) {
                        showToast('Bulk command failed', 'error')
                    }
                    setBulkLoading(false)
                }
            })
            return
        }

        // For start command, show confirmation with option to enable auto-login/revive
        setConfirmAction({
            title: 'Login Selected Bots',
            message: `Login ${affectedSips.length} bots?`,
            checkboxes: [{ label: 'Enable Auto-Login/Revive', checked: false }],
            confirmText: 'Login',
            onConfirm: async (checks) => {
                setConfirmAction(null)
                setBulkLoading(true)
                console.log(`[DialerBotsTab] bulkCommand: command=${command}, dialerId=${dialerId}, sips=${affectedSips.slice(0,5).join(',')}...`)
                try {
                    // Enable auto-login/revive if checked
                    if (checks && checks[0]) {
                        for (const bot of affectedBots) {
                            if (!bot.auto_login) await monitoringApi.setAutoLogin(bot.sip_id, true, dialerId)
                            if (!bot.auto_revive) await monitoringApi.setAutoRevive(bot.sip_id, true, dialerId)
                        }
                        setBots(prev => prev.map(b =>
                            affectedSips.includes(b.sip_id) ? { ...b, auto_login: 1, auto_revive: 1 } : b
                        ))
                    }
                    await monitoringApi.sendCommand(affectedSips, command, dialerId)
                    setCommandState(prev => {
                        const next = { ...prev }
                        affectedSips.forEach(sipId => { next[sipId] = { cmd: command, status: 'Sending...' } })
                        return next
                    })
                    setSelectedBots(new Set())
                    showToast(`Login command sent to ${affectedSips.length} bots`, 'success')
                    setTimeout(() => {
                        setCommandState(prev => {
                            const next = { ...prev }
                            affectedSips.forEach(sipId => {
                                if (next[sipId]?.status === 'Sending...') delete next[sipId]
                            })
                            return next
                        })
                    }, 15000)
                } catch (err) {
                    showToast('Bulk command failed', 'error')
                }
                setBulkLoading(false)
            }
        })
    }

    const bulkVMAction = async (action) => {
        if (selectedBots.size === 0) return
        setBulkLoading(true)
        const botsWithVM = bots.filter(b => selectedBots.has(b.sip_id) && b.server_ip && b.vmid)
        if (botsWithVM.length === 0) {
            showToast('No VMs linked', 'error')
            setBulkLoading(false)
            return
        }

        const byServer = {}
        botsWithVM.forEach(bot => {
            if (!byServer[bot.server_ip]) byServer[bot.server_ip] = []
            byServer[bot.server_ip].push(bot)
        })

        let success = 0
        for (const [serverIP, serverBots] of Object.entries(byServer)) {
            try {
                await monitoringApi.bulkVMAction(serverIP, serverBots.map(b => b.vmid), action)
                success += serverBots.length
            } catch (err) { }
        }

        if (success > 0) showToast(`${action} sent to ${success} VMs`, 'success')
        setSelectedBots(new Set())
        setBulkLoading(false)
    }

    const getStatusClass = (s) => {
        if (s === 'active' || s === 'running') return 'active'
        if (s === 'dial_level_0') return 'dial_level_0'
        if (s === 'waiting') return 'waiting'
        return 'offline'
    }

    const getStatusLabel = (s) => {
        if (s === 'active' || s === 'running') return 'Active'
        if (s === 'dial_level_0') return 'Dial Lvl 0'
        if (s === 'waiting') return 'Logged Out'
        return 'Offline'
    }

    const getHBClass = (date) => {
        if (!date) return ''
        const diff = (Date.now() - new Date(date)) / 1000
        if (diff < 120) return 'recent'
        return 'stale'
    }

    // Delete bot handler
    const handleDeleteBot = (bot) => {
        setDeleteBotDialog({ type: 'single', bot })
    }

    // Bulk delete bots handler
    const handleBulkDeleteBots = () => {
        if (selectedBots.size === 0) {
            showToast('Select bots first', 'error')
            return
        }
        const botsToDelete = bots.filter(b => selectedBots.has(b.sip_id))
        setDeleteBotDialog({ type: 'bulk', bots: botsToDelete })
    }

    // Execute delete
    const executeDeleteBot = async (deleteCallRecords, deleteVMs) => {
        if (!deleteBotDialog) return
        setDeleteBotDialog(null)
        setBulkLoading(true)

        try {
            if (deleteBotDialog.type === 'single') {
                const result = await monitoringApi.deleteBot(
                    deleteBotDialog.bot.sip_id,
                    dialerId,
                    deleteCallRecords,
                    deleteVMs
                )
                if (result.data?.success) {
                    const vmInfo = deleteVMs && result.data.deleted?.vms ? ` (VM deleted)` : ''
                    showToast(`Bot ${deleteBotDialog.bot.sip_id} deleted${vmInfo}`, 'success')
                    fetchBots()
                } else {
                    showToast(result.data?.error || 'Delete failed', 'error')
                }
            } else if (deleteBotDialog.type === 'bulk') {
                const sipIds = deleteBotDialog.bots.map(b => b.sip_id)
                const result = await monitoringApi.bulkDeleteBots(sipIds, dialerId, deleteCallRecords, deleteVMs)
                if (result.data?.success) {
                    const vmInfo = deleteVMs && result.data.deleted?.vms ? `, ${result.data.deleted.vms} VMs deleted` : ''
                    const vmErrors = result.data.deleted?.vm_errors?.length ? ` (${result.data.deleted.vm_errors.length} VM errors)` : ''
                    showToast(`Deleted ${result.data.deleted?.succeeded || sipIds.length} bots${vmInfo}${vmErrors}`, 'success')
                    setSelectedBots(new Set())
                    fetchBots()
                } else {
                    showToast(result.data?.error || 'Bulk delete failed', 'error')
                }
            }
        } catch (err) {
            showToast(`Delete failed: ${err.message}`, 'error')
        }

        setBulkLoading(false)
    }

    return (
        <div className="dialer-bots-tab">
            {/* Toasts */}
            <div className="dbt-toast-container">
                {toasts.map(t => <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />)}
            </div>

            {/* Confirm */}
            {confirmAction && (
                <ConfirmDialog
                    title={confirmAction.title}
                    message={confirmAction.message}
                    checkboxes={confirmAction.checkboxes || []}
                    confirmText={confirmAction.confirmText}
                    onConfirm={confirmAction.onConfirm}
                    onCancel={() => setConfirmAction(null)}
                />
            )}

            {/* Stats Row */}
            <div className="dbt-stats-row">
                <div className="dbt-stat-cards">
                    {[
                        { key: 'active', label: 'ACTIVE', count: statusStats.active, icon: Activity, cls: 'active' },
                        { key: 'dial_level_0', label: 'DIAL 0', count: statusStats.dial_level_0, icon: AlertTriangle, cls: 'dial0' },
                        { key: 'waiting', label: 'LOGGED OUT', count: statusStats.waiting, icon: Clock, cls: 'waiting' },
                        { key: 'offline', label: 'OFFLINE', count: statusStats.offline, icon: WifiOff, cls: 'offline' }
                    ].map(s => (
                        <button
                            key={s.key}
                            className={`dbt-stat-card ${s.cls} ${selectedStatus === s.key ? 'selected' : ''}`}
                            onClick={() => setSelectedStatus(selectedStatus === s.key ? '' : s.key)}
                        >
                            <div className="dbt-stat-icon"><s.icon size={14} /></div>
                            <span className="dbt-stat-value">{s.count}</span>
                            <span className="dbt-stat-label">{s.label}</span>
                        </button>
                    ))}
                </div>

                <div className="dbt-call-stats">
                    <div className="dbt-call-stat"><Phone size={14} /><span>{totals.calls}</span></div>
                    <div className="dbt-call-stat xfer"><PhoneForwarded size={14} /><span>{totals.xfer}</span></div>
                    <div className="dbt-call-stat am"><Voicemail size={14} /><span>{totals.am}</span></div>
                    <div className="dbt-call-stat ni"><PhoneOff size={14} /><span>{totals.ni}</span></div>
                    <div className="dbt-call-stat na"><PhoneMissed size={14} /><span>{totals.na}</span></div>
                    <div className="dbt-call-stat dnc"><Ban size={14} /><span>{totals.dnc}</span></div>
                </div>
            </div>

            {/* Action Toolbar */}
            <div className="dbt-toolbar">
                <div className="dbt-toolbar-left">
                    {/* Selection info */}
                    {selectedBots.size > 0 && (
                        <div className="dbt-selection-badge">
                            <CheckSquare size={14} />
                            <span>{selectedBots.size} selected</span>
                            <button onClick={() => setSelectedBots(new Set())}><X size={12} /></button>
                        </div>
                    )}

                    {/* Bulk Actions Dropdown */}
                    <div className="dbt-dropdown-wrapper">
                        <button
                            className="dbt-toolbar-btn"
                            onClick={() => { setShowBulkDropdown(!showBulkDropdown); setShowVMDropdown(false) }}
                        >
                            <Settings size={14} />
                            <span>Bulk Actions</span>
                            <ChevronDown size={12} />
                        </button>
                        {showBulkDropdown && (
                            <div className="dbt-dropdown-menu" onClick={() => setShowBulkDropdown(false)}>
                                <button onClick={() => bulkCommand('start')} disabled={bulkLoading || selectedBots.size === 0}>
                                    <Play size={14} /> Login Selected
                                </button>
                                <button onClick={() => bulkCommand('stop')} disabled={bulkLoading || selectedBots.size === 0}>
                                    <Square size={14} /> Logout Selected
                                </button>
                                <div className="dbt-dropdown-divider" />
                                <button onClick={() => openBulkVoiceSetModal()} disabled={selectedBots.size === 0}>
                                    <Mic size={14} /> Edit Voice Sets
                                </button>
                                <button onClick={() => openBulkTranscriberModal()} disabled={selectedBots.size === 0}>
                                    <AudioWaveform size={14} /> Change Transcriber
                                </button>
                                <div className="dbt-dropdown-divider" />
                                <button onClick={() => toggleSelectAll()}>
                                    <CheckSquare size={14} /> {selectedBots.size === filteredBots.length ? 'Deselect All' : 'Select All'}
                                </button>
                                <button onClick={() => { setShowRangeModal(true); setShowBulkDropdown(false) }}>
                                    <ArrowUpDown size={14} /> Select Range
                                </button>
                                <div className="dbt-dropdown-divider" />
                                <button
                                    onClick={() => handleBulkDeleteBots()}
                                    disabled={bulkLoading || selectedBots.size === 0}
                                    className="dbt-dropdown-danger"
                                >
                                    <Trash2 size={14} /> Delete Selected
                                </button>
                            </div>
                        )}
                    </div>

                    {/* VM Actions Dropdown */}
                    <div className="dbt-dropdown-wrapper">
                        <button
                            className="dbt-toolbar-btn"
                            onClick={() => { setShowVMDropdown(!showVMDropdown); setShowBulkDropdown(false) }}
                        >
                            <Server size={14} />
                            <span>VM Actions</span>
                            <ChevronDown size={12} />
                        </button>
                        {showVMDropdown && (
                            <div className="dbt-dropdown-menu" onClick={() => setShowVMDropdown(false)}>
                                <button onClick={() => bulkVMAction('start')} disabled={bulkLoading || selectedBots.size === 0}>
                                    <Power size={14} /> Start VMs
                                </button>
                                <button onClick={() => bulkVMAction('restart')} disabled={bulkLoading || selectedBots.size === 0}>
                                    <RotateCcw size={14} /> Restart VMs
                                </button>
                                <button onClick={() => bulkVMAction('stop')} disabled={bulkLoading || selectedBots.size === 0}>
                                    <PowerOff size={14} /> Stop VMs
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="dbt-toolbar-right">
                    {/* Auto ON Toggle */}
                    <div className="dbt-auto-toggle-group">
                        <span className="dbt-auto-label">
                            Auto ON
                            <span className="dbt-auto-count">{autoStats.autoLoginOn}/{bots.length}</span>
                        </span>
                        <button
                            className={`dbt-toggle-btn ${autoStats.autoLoginOn === bots.length ? 'all-on' : autoStats.autoLoginOn > 0 ? 'partial' : ''}`}
                            onClick={() => bulkAutoLogin(autoStats.autoLoginOn < bots.length)}
                            disabled={bulkLoading}
                            title={autoStats.autoLoginOn === bots.length ? 'Disable Auto-Login for all' : 'Enable Auto-Login for all'}
                        >
                            {autoStats.autoLoginOn === bots.length ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        </button>
                    </div>

                    {/* Auto Revive Toggle */}
                    <div className="dbt-auto-toggle-group">
                        <span className="dbt-auto-label">
                            Auto Revive
                            <span className="dbt-auto-count">{autoStats.autoReviveOn}/{bots.length}</span>
                        </span>
                        <button
                            className={`dbt-toggle-btn revive ${autoStats.autoReviveOn === bots.length ? 'all-on' : autoStats.autoReviveOn > 0 ? 'partial' : ''}`}
                            onClick={() => bulkAutoRevive(autoStats.autoReviveOn < bots.length)}
                            disabled={bulkLoading}
                            title={autoStats.autoReviveOn === bots.length ? 'Disable Auto-Revive for all' : 'Enable Auto-Revive for all'}
                        >
                            {autoStats.autoReviveOn === bots.length ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="dbt-table-container">
                {loading ? (
                    <div className="dbt-loading"><RefreshCw size={24} /><span>Loading bots...</span></div>
                ) : paginatedBots.length === 0 ? (
                    <div className="dbt-empty"><Bot size={40} /><p>No bots found</p></div>
                ) : (
                    <table className="dbt-table">
                        <thead>
                            <tr>
                                <th className="dbt-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedBots.size === filteredBots.length && filteredBots.length > 0}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th className="dbt-th-auto" title="Auto Login"><ToggleRight size={12} /></th>
                                <th className="dbt-th-auto" title="Auto Revive"><RefreshCcw size={12} /></th>
                                <th>SIP</th>
                                <th>Status</th>
                                <th className="stat-col" title="Total Calls"><Phone size={12} /></th>
                                <th className="stat-col" title="Transfers"><PhoneForwarded size={12} /></th>
                                <th className="stat-col" title="Answering Machine"><Voicemail size={12} /></th>
                                <th className="stat-col" title="Not Interested"><PhoneOff size={12} /></th>
                                <th className="stat-col" title="No Answer"><PhoneMissed size={12} /></th>
                                <th className="stat-col" title="Do Not Call"><Ban size={12} /></th>
                                <th className="stat-col" title="Not Qualified"><UserX size={12} /></th>
                                <th className="stat-col" title="Call Status"><Phone size={12} /></th>
                                <th className="center">Step</th>
                                <th className="dbt-th-voice" title="Voice Sets"><Mic size={12} /></th>
                                <th className="center">Mode</th>
                                <th className="center" title="Git Branch: production=main, development=dev">Branch</th>
                                <th className="center" title="Live Logs (Dev Mode)"><Terminal size={12} /></th>
                                <th className="center">HB</th>
                                <th className="center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedBots.map(bot => {
                                const status = bot.effective_status || 'offline'
                                const cmdState = commandState[bot.sip_id]
                                const isSelected = selectedBots.has(bot.sip_id)
                                const isActive = status === 'active' || status === 'running' || status === 'dial_level_0'
                                const rowClass = status === 'active' || status === 'running' ? 'row-active' :
                                    status === 'dial_level_0' ? 'row-dial0' :
                                        status === 'waiting' ? 'row-waiting' : ''

                                return (
                                    <tr key={bot.sip_id} className={`${rowClass} ${isSelected ? 'selected' : ''}`}>
                                        <td className="dbt-checkbox">
                                            <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(bot.sip_id)} />
                                        </td>
                                        {/* Auto Login */}
                                        <td className="dbt-td-auto">
                                            <button
                                                className={`dbt-auto-btn ${bot.auto_login ? 'on' : ''}`}
                                                onClick={() => toggleAutoLogin(bot)}
                                                title={bot.auto_login ? 'Auto-login enabled - click to disable' : 'Auto-login disabled - click to enable'}
                                            >
                                                {bot.auto_login ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                            </button>
                                        </td>
                                        {/* Auto Revive */}
                                        <td className="dbt-td-auto">
                                            <button
                                                className={`dbt-revive-btn ${bot.auto_revive ? 'on' : ''} ${!bot.auto_login ? 'disabled' : ''}`}
                                                onClick={() => toggleAutoRevive(bot)}
                                                title={!bot.auto_login ? 'Enable Auto-Login first' : bot.auto_revive ? 'Auto-revive enabled' : 'Auto-revive disabled'}
                                            >
                                                <RefreshCcw size={14} />
                                            </button>
                                        </td>
                                        <td><span className="dbt-sip">{bot.sip_id}</span></td>
                                        <td>
                                            {cmdState ? (
                                                <span className={`dbt-status ${cmdState.error ? 'error' : 'loading'}`}>
                                                    {!cmdState.error && <RefreshCw size={10} className="spinning" />}
                                                    {cmdState.status}
                                                </span>
                                            ) : (
                                                <span className={`dbt-status ${getStatusClass(status)}`}>
                                                    {getStatusLabel(status)}
                                                </span>
                                            )}
                                        </td>
                                        <td className={`dbt-stat-cell ${bot.calls > 0 ? 'highlight' : ''}`}>{bot.calls || 0}</td>
                                        <td className={`dbt-stat-cell xfer`}>{bot.xfer || 0}</td>
                                        <td className={`dbt-stat-cell am`}>{bot.am || 0}</td>
                                        <td className={`dbt-stat-cell ni`}>{bot.ni || 0}</td>
                                        <td className={`dbt-stat-cell na`}>{bot.na || 0}</td>
                                        <td className={`dbt-stat-cell dnc`}>{bot.dnc || 0}</td>
                                        <td className={`dbt-stat-cell dnq`}>{bot.dnq || 0}</td>
                                        {/* Call Status */}
                                        <td className="dbt-td-call-status">
                                            {bot.current_phone ? (
                                                <span className="dbt-call-status in-call">
                                                    <Phone size={10} /> In Call
                                                </span>
                                            ) : '-'}
                                        </td>
                                        {/* Step */}
                                        <td className="center">{bot.current_step || '-'}</td>
                                        {/* Voice Sets */}
                                        <td className="dbt-td-voice">
                                            <button
                                                className="dbt-voice-btn"
                                                onClick={() => setVoiceSetBot(bot)}
                                                title="Edit Voice Sets"
                                                disabled={!bot.license_id}
                                            >
                                                <Mic size={12} />
                                                <span className="dbt-voice-count">
                                                    {(bot.voice_sets_male || 0) + (bot.voice_sets_female || 0)}
                                                </span>
                                            </button>
                                        </td>
                                        {/* Mode (Transcriber) */}
                                        <td className="center">
                                            <span className={`dbt-mode ${bot.transcriber_type === 'HYBRID' ? '' : 'vosk'}`}>
                                                {bot.transcriber_type === 'HYBRID' ? <><AudioWaveform size={10} /> HYB</> : <><Mic size={10} /> VSK</>}
                                            </span>
                                        </td>
                                        {/* Bot Mode (Git Branch) */}
                                        <td className="center">
                                            {(() => {
                                              const MODE_META = {
                                                production:   { label: 'PROD',  cls: 'dbt-branch-prod',  branch: 'main',           hint: 'Production (main branch)' },
                                                development:  { label: 'DEV',   cls: 'dbt-branch-dev',   branch: 'dev',            hint: 'Development (dev branch)' },
                                                test:         { label: 'TEST',  cls: 'dbt-branch-test',  branch: 'test',           hint: 'Test (test branch)' },
                                                linux:        { label: 'LNX',   cls: 'dbt-branch-linux', branch: 'linux-bot',      hint: 'Linux (linux-bot branch) — LXC stable' },
                                                'linux-test': { label: 'LNX-T', cls: 'dbt-branch-linux-test', branch: 'linux-bot-test', hint: 'Linux Test (linux-bot-test repo) — LXC pre-prod' }
                                              }
                                              const isLxc = bot.vm_type === 'lxc'
                                              const cycle = isLxc
                                                ? ['linux', 'linux-test']
                                                : ['production', 'development', 'test']
                                              const meta = MODE_META[bot.bot_mode] || MODE_META.production
                                              return (
                                                <button
                                                  className={`dbt-branch-btn ${meta.cls}`}
                                                  onClick={async () => {
                                                    if (!bot.license_id) {
                                                      showToast('No license found for this bot', 'error')
                                                      return
                                                    }
                                                    const cur = cycle.indexOf(bot.bot_mode || cycle[0])
                                                    const newMode = cycle[(cur + 1) % cycle.length]
                                                    try {
                                                      await machineApi.setBotMode(bot.license_id, newMode)
                                                      setBots(prev => prev.map(b =>
                                                        b.sip_id === bot.sip_id ? { ...b, bot_mode: newMode } : b
                                                      ))
                                                      showToast(`Bot mode: ${newMode} (${MODE_META[newMode].branch}). Restart VM to apply.`, 'success')
                                                    } catch (err) {
                                                      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to update bot mode'
                                                      showToast(msg, 'error')
                                                    }
                                                  }}
                                                  title={`${meta.hint} — Click to cycle`}
                                                >
                                                  {meta.label}
                                                </button>
                                              )
                                            })()}
                                        </td>
                                        {/* Live Logs - Dev/Test/Linux modes */}
                                        <td className="dbt-td-logs">
                                            {(bot.bot_mode === 'development' || bot.bot_mode === 'test' || bot.bot_mode === 'linux' || bot.bot_mode === 'linux-test') ? (
                                                <div className="dbt-logs-controls">
                                                    {/* Streaming Toggle */}
                                                    <button
                                                        className={`dbt-stream-toggle ${streamingSessions[bot.sip_id]?.isStreaming ? 'streaming' : ''}`}
                                                        onClick={() => toggleStreaming(bot)}
                                                        title={streamingSessions[bot.sip_id]?.isStreaming ? 'Stop Streaming' : 'Start Streaming'}
                                                    >
                                                        <Radio size={12} />
                                                    </button>
                                                    {/* View Logs Button */}
                                                    <button
                                                        className={`dbt-view-logs-btn ${streamingSessions[bot.sip_id]?.logs?.length > 0 ? 'has-logs' : ''}`}
                                                        onClick={() => setLiveLogsBot(bot)}
                                                        title={`View Logs${streamingSessions[bot.sip_id]?.logs?.length ? ` (${streamingSessions[bot.sip_id].logs.length})` : ''}`}
                                                    >
                                                        <Terminal size={12} />
                                                        {streamingSessions[bot.sip_id]?.logs?.length > 0 && (
                                                            <span className="dbt-logs-count">{streamingSessions[bot.sip_id].logs.length}</span>
                                                        )}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="dbt-no-logs">-</span>
                                            )}
                                        </td>
                                        <td className="center">
                                            <span className={`dbt-hb ${getHBClass(bot.last_heartbeat)}`}>
                                                {formatTimeAgo(bot.last_heartbeat)}
                                            </span>
                                        </td>
                                        <td className="dbt-td-actions">
                                            <div className="dbt-action-group">
                                                {/* Start/Stop Bot */}
                                                {isActive ? (
                                                    <button
                                                        className="dbt-action-btn dbt-action-stop"
                                                        onClick={() => sendCommand(bot.sip_id, 'stop')}
                                                        disabled={!!cmdState}
                                                        title="Stop Bot"
                                                    >
                                                        <Square size={11} />
                                                    </button>
                                                ) : (
                                                    <button
                                                        className={`dbt-action-btn dbt-action-start ${status === 'offline' ? 'dbt-action-disabled' : ''}`}
                                                        onClick={() => sendCommand(bot.sip_id, 'start')}
                                                        disabled={!!cmdState || status === 'offline'}
                                                        title={status === 'offline' ? 'Start VM first' : 'Start Bot'}
                                                    >
                                                        <Play size={11} />
                                                    </button>
                                                )}
                                                {/* Transcriber */}
                                                <button
                                                    className="dbt-action-btn dbt-action-transcriber"
                                                    onClick={() => setTranscriberBot(bot)}
                                                    title="Change Transcriber"
                                                >
                                                    <AudioWaveform size={11} />
                                                </button>
                                                {/* Voice Sets */}
                                                <button
                                                    className="dbt-action-btn dbt-action-voice"
                                                    onClick={() => setVoiceSetBot(bot)}
                                                    title="Edit Voice Sets"
                                                >
                                                    <Volume2 size={11} />
                                                </button>
                                                {/* Call Logs - switches to Call Logs tab */}
                                                <button
                                                    className="dbt-action-btn dbt-action-logs"
                                                    onClick={() => {
                                                        if (onFilterSip) onFilterSip(bot.sip_id)
                                                        if (onTabChange) onTabChange('logs')
                                                    }}
                                                    title="View Call Logs"
                                                >
                                                    <FileText size={11} />
                                                </button>
                                                {/* Crash Report */}
                                                <button
                                                    className="dbt-action-btn dbt-action-crash"
                                                    onClick={() => setCrashReportBot(bot)}
                                                    title="View Crash Reports"
                                                >
                                                    <FileWarning size={11} />
                                                </button>
                                                {/* VM Actions - inline */}
                                                {bot.server_ip && bot.vmid && (
                                                    <>
                                                        <button
                                                            className="dbt-action-btn dbt-action-vm-start"
                                                            onClick={() => vmAction('start', bot)}
                                                            disabled={!!commandState[bot.sip_id]}
                                                            title="Start VM"
                                                        >
                                                            <Power size={11} />
                                                        </button>
                                                        <button
                                                            className="dbt-action-btn dbt-action-vm-restart"
                                                            onClick={() => vmAction('restart', bot)}
                                                            disabled={!!commandState[bot.sip_id]}
                                                            title="Restart VM"
                                                        >
                                                            <RotateCcw size={11} />
                                                        </button>
                                                        {bot.novnc_port && bot.server_ip && !/[a-zA-Z]/.test(bot.server_ip) && (
                                                            <button
                                                                className="dbt-action-btn dbt-action-vnc"
                                                                onClick={() => window.open(`http://${bot.server_ip}:${bot.novnc_port}/vnc.html?autoconnect=true&resize=remote`, '_blank')}
                                                                title="Open VNC"
                                                            >
                                                                <Monitor size={11} />
                                                            </button>
                                                        )}
                                                        <button
                                                            className="dbt-action-btn dbt-action-vm-link"
                                                            onClick={() => setLinkVMBot({ ...bot, dialer_id: dialerId, dialer_name: dialerName })}
                                                            title="Change VM Link"
                                                        >
                                                            <Link2 size={11} />
                                                        </button>
                                                    </>
                                                )}
                                                {bot.server_ip && !bot.vmid && (
                                                    <button
                                                        className="dbt-action-btn dbt-action-vm-link"
                                                        onClick={() => setLinkVMBot({ ...bot, dialer_id: dialerId, dialer_name: dialerName })}
                                                        title="Link VM"
                                                    >
                                                        <Link2 size={11} />
                                                    </button>
                                                )}
                                                {/* Delete Bot */}
                                                <button
                                                    className="dbt-action-btn dbt-action-delete"
                                                    onClick={() => handleDeleteBot(bot)}
                                                    title="Delete Bot"
                                                >
                                                    <Trash2 size={11} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="dbt-pagination">
                    <div className="dbt-page-info">
                        Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filteredBots.length)} of {filteredBots.length}
                    </div>
                    <div className="dbt-page-controls">
                        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}>
                            {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
                        </select>
                        <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                            <ChevronLeft size={14} />
                        </button>
                        <span>{currentPage} / {totalPages}</span>
                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                            <ChevronRight size={14} />
                        </button>
                        <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
                    </div>
                </div>
            )}

            {/* Transcriber Modal */}
            {transcriberBot && (
                <TranscriberModal
                    bot={transcriberBot}
                    bulkBots={transcriberBulkMode ? transcriberBulkBots : null}
                    bulkMode={transcriberBulkMode}
                    onClose={() => {
                        setTranscriberBot(null)
                        setTranscriberBulkMode(false)
                        setTranscriberBulkBots([])
                    }}
                    onSave={async (transcriberType) => {
                        try {
                            if (transcriberBulkMode && transcriberBulkBots.length > 0) {
                                // Bulk save
                                let success = 0
                                for (const bot of transcriberBulkBots) {
                                    try {
                                        await monitoringApi.updateLicenseTranscriber(bot.license_id, transcriberType)
                                        success++
                                    } catch (err) { }
                                }
                                setBots(prev => prev.map(b =>
                                    transcriberBulkBots.find(tb => tb.sip_id === b.sip_id)
                                        ? { ...b, transcriber_type: transcriberType }
                                        : b
                                ))
                                showToast(`Transcriber updated for ${success}/${transcriberBulkBots.length} bots`, 'success')
                                setSelectedBots(new Set())
                            } else {
                                await monitoringApi.updateLicenseTranscriber(transcriberBot.license_id, transcriberType)
                                setBots(prev => prev.map(b => b.sip_id === transcriberBot.sip_id ? { ...b, transcriber_type: transcriberType } : b))
                                showToast(`Transcriber updated to ${transcriberType}`, 'success')
                            }
                            setTranscriberBot(null)
                            setTranscriberBulkMode(false)
                            setTranscriberBulkBots([])
                        } catch (err) {
                            showToast('Failed to update transcriber', 'error')
                        }
                    }}
                />
            )}

            {/* VoiceSet Modal */}
            {voiceSetBot && (
                <VoiceSetModal
                    bot={voiceSetBot}
                    bulkBots={voiceSetBulkMode ? voiceSetBulkBots : null}
                    bulkMode={voiceSetBulkMode}
                    showToast={showToast}
                    onClose={() => {
                        setVoiceSetBot(null)
                        setVoiceSetBulkMode(false)
                        setVoiceSetBulkBots([])
                    }}
                    onSave={async (selectedPool) => {
                        if (voiceSetBulkMode && voiceSetBulkBots.length > 0) {
                            // Bulk save
                            let success = 0
                            for (const bot of voiceSetBulkBots) {
                                try {
                                    await monitoringApi.updateLicenseVoicePool(bot.license_id, selectedPool)
                                    success++
                                } catch (err) { }
                            }
                            showToast(`Voice sets updated for ${success}/${voiceSetBulkBots.length} bots. Restart VMs to apply.`, 'success')
                            setSelectedBots(new Set())
                        } else {
                            showToast('Voice sets updated. Restart VM to apply.', 'success')
                        }
                        setVoiceSetBot(null)
                        setVoiceSetBulkMode(false)
                        setVoiceSetBulkBots([])
                    }}
                />
            )}

            {/* Link VM Modal */}
            {linkVMBot && (
                <LinkVMModal
                    bot={linkVMBot}
                    onClose={() => setLinkVMBot(null)}
                    onLinked={() => {
                        setLinkVMBot(null)
                        fetchBots()
                        showToast('VM linked successfully', 'success')
                    }}
                />
            )}

            {/* Crash Report Modal */}
            {crashReportBot && (
                <CrashReportModal
                    bot={crashReportBot}
                    onClose={() => setCrashReportBot(null)}
                />
            )}

            {/* Live Logs Modal - Dev mode bots only */}
            {liveLogsBot && (
                <LiveLogsModal
                    bot={liveLogsBot}
                    logs={streamingSessions[liveLogsBot.sip_id]?.logs || []}
                    isStreaming={streamingSessions[liveLogsBot.sip_id]?.isStreaming || false}
                    connected={streamingSessions[liveLogsBot.sip_id]?.connected || false}
                    onToggleStreaming={() => toggleStreaming(liveLogsBot)}
                    onClearLogs={() => clearBotLogs(liveLogsBot.sip_id)}
                    onAddLog={(log) => addBotLog(liveLogsBot.sip_id, log)}
                    onClose={() => setLiveLogsBot(null)}
                />
            )}

            {/* Delete Bot Dialog */}
            {deleteBotDialog && (
                <DeleteBotDialog
                    dialog={deleteBotDialog}
                    onClose={() => setDeleteBotDialog(null)}
                    onConfirm={executeDeleteBot}
                />
            )}

            {/* Select Range Modal */}
            {showRangeModal && (
                <div className="dbt-modal-overlay" onClick={() => setShowRangeModal(false)}>
                    <div className="dbt-modal dbt-range-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="dbt-modal-header">
                            <h3><ArrowUpDown size={16} /> Select SIP Range</h3>
                            <button className="dbt-modal-close" onClick={() => setShowRangeModal(false)}><X size={16} /></button>
                        </div>
                        <div className="dbt-modal-body">
                            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 16 }}>
                                Select bots by ID range. Supports numeric (8026-8030) and prefixed IDs (Agent1-Agent20).
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>From</label>
                                    <input
                                        type="text"
                                        className="dbt-range-input"
                                        value={rangeFrom}
                                        onChange={(e) => setRangeFrom(e.target.value)}
                                        placeholder="e.g. 8026 or Agent1"
                                        autoFocus
                                        onKeyDown={(e) => e.key === 'Enter' && selectRange()}
                                    />
                                </div>
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem', paddingTop: 18 }}>to</span>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>To</label>
                                    <input
                                        type="text"
                                        className="dbt-range-input"
                                        value={rangeTo}
                                        onChange={(e) => setRangeTo(e.target.value)}
                                        placeholder="e.g. 8030 or Agent20"
                                        onKeyDown={(e) => e.key === 'Enter' && selectRange()}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="dbt-modal-footer">
                            <button className="dbt-modal-btn secondary" onClick={() => setShowRangeModal(false)}>Cancel</button>
                            <button className="dbt-modal-btn primary" onClick={selectRange} disabled={!rangeFrom || !rangeTo}>
                                <CheckSquare size={14} /> Select Range
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// Transcriber Modal Component
function TranscriberModal({ bot, bulkBots, bulkMode, onClose, onSave }) {
    const [selected, setSelected] = useState(bot.transcriber_type || 'VOSK')
    const [saving, setSaving] = useState(false)

    const TRANSCRIBER_OPTIONS = [
        { value: 'VOSK', label: 'VOSK', description: 'Fast, local processing' },
        { value: 'VOSK_V15L', label: 'VOSK V15L', description: 'Accurate, larger model' },
        { value: 'HYBRID', label: 'Hybrid', description: 'VOSK + Whisper for accuracy' },
        { value: 'WHISPER_CPU', label: 'Whisper CPU', description: 'Whisper Tiny (Local)' }
    ]

    const handleSave = async () => {
        setSaving(true)
        await onSave(selected)
        setSaving(false)
    }

    return (
        <div className="dbt-modal-overlay" onClick={onClose}>
            <div className="dbt-modal" onClick={e => e.stopPropagation()}>
                <div className="dbt-modal-header">
                    <h3><AudioWaveform size={18} /> Change Transcriber</h3>
                    <button className="dbt-modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="dbt-modal-body">
                    <p className="dbt-modal-subtitle">
                        {bulkMode ? `${bulkBots?.length || 0} Bots Selected` : `Bot: ${bot.sip_id}`}
                    </p>
                    <div className="dbt-option-list">
                        {TRANSCRIBER_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                className={`dbt-option-item ${selected === opt.value ? 'selected' : ''}`}
                                onClick={() => setSelected(opt.value)}
                            >
                                <div className="dbt-option-radio">{selected === opt.value && <Check size={12} />}</div>
                                <div className="dbt-option-content">
                                    <span className="dbt-option-label">{opt.label}</span>
                                    <span className="dbt-option-desc">{opt.description}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
                <div className="dbt-modal-footer">
                    <button className="dbt-modal-btn secondary" onClick={onClose}>Cancel</button>
                    <button className="dbt-modal-btn primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// VoiceSet Modal Component - Multi-select voice pool
function VoiceSetModal({ bot, bulkBots, bulkMode, showToast, onClose, onSave }) {
    const [voiceSets, setVoiceSets] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedPool, setSelectedPool] = useState([])
    const [saving, setSaving] = useState(false)
    const [playingId, setPlayingId] = useState(null)
    const [audioRef] = useState(() => new Audio())

    useEffect(() => {
        const loadData = async () => {
            if (!bot.license_id) {
                setLoading(false)
                return
            }
            try {
                // Fetch all voice sets
                const vsRes = await voiceSetApi.getAll()
                setVoiceSets(vsRes.data || [])

                // Fetch current license voice pool
                const poolRes = await monitoringApi.getLicenseVoicePool(bot.license_id)
                if (poolRes.data?.success) {
                    setSelectedPool(poolRes.data.license?.voice_set_pool || [])
                }
            } catch (err) {
                console.error('Failed to fetch voice sets:', err)
            } finally {
                setLoading(false)
            }
        }
        loadData()

        // Cleanup audio on unmount
        return () => {
            audioRef.pause()
            audioRef.src = ''
        }
    }, [bot.license_id])

    const toggleVoiceSet = (vsId) => {
        setSelectedPool(prev => {
            if (prev.includes(vsId)) {
                return prev.filter(id => id !== vsId)
            } else {
                return [...prev, vsId]
            }
        })
    }

    const previewVoiceSet = async (vsId, e) => {
        e.stopPropagation()

        if (playingId === vsId) {
            audioRef.pause()
            audioRef.src = ''
            setPlayingId(null)
            return
        }

        const vs = voiceSets.find(v => v.id === vsId)
        if (vs?.sample_url) {
            setPlayingId(vsId)
            audioRef.src = vs.sample_url
            audioRef.onended = () => setPlayingId(null)
            audioRef.onerror = () => {
                setPlayingId(null)
                if (showToast) showToast('Failed to play audio', 'error')
            }
            try {
                await audioRef.play()
            } catch {
                setPlayingId(null)
            }
        } else {
            if (showToast) showToast('No sample audio available', 'info')
        }
    }

    const handleSave = async () => {
        if (!bot.license_id) return
        setSaving(true)
        try {
            if (bulkMode) {
                // In bulk mode, let parent handle the save for all bots
                await onSave(selectedPool)
            } else {
                // Single bot mode - save directly
                await monitoringApi.updateLicenseVoicePool(bot.license_id, selectedPool)
                onSave(selectedPool)
            }
        } catch (err) {
            console.error('Failed to save voice sets:', err)
        }
        setSaving(false)
    }

    return (
        <div className="dbt-modal-overlay" onClick={onClose}>
            <div className="dbt-modal dbt-modal-lg" onClick={e => e.stopPropagation()}>
                <div className="dbt-modal-header">
                    <h3><Volume2 size={18} /> Voice Sets: {bulkMode ? `${bulkBots?.length || 0} Bots` : bot.sip_id}</h3>
                    <button className="dbt-modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="dbt-modal-body">
                    {!bot.license_id ? (
                        <div className="dbt-modal-empty">
                            <AlertCircle size={20} />
                            <span>Bot has no license configured</span>
                        </div>
                    ) : loading ? (
                        <div className="dbt-modal-loading"><RefreshCw size={20} className="spinning" /> Loading voice sets...</div>
                    ) : voiceSets.length === 0 ? (
                        <div className="dbt-modal-empty">No voice sets available</div>
                    ) : (
                        <>
                            <p className="dbt-modal-hint">Select voice sets to rotate through on each call. Click play to preview.</p>
                            <div className="dbt-voice-grid">
                                {voiceSets.map(vs => (
                                    <div
                                        key={vs.id}
                                        className={`dbt-voice-card ${selectedPool.includes(vs.id) ? 'selected' : ''}`}
                                        onClick={() => toggleVoiceSet(vs.id)}
                                    >
                                        <button
                                            className="dbt-voice-preview"
                                            onClick={(e) => previewVoiceSet(vs.id, e)}
                                            title="Preview voice"
                                        >
                                            {playingId === vs.id ? <Square size={12} /> : <Play size={12} />}
                                        </button>
                                        <span className="dbt-voice-name">{vs.name}</span>
                                        {vs.gender && <span className="dbt-voice-gender">{vs.gender}</span>}
                                        {selectedPool.includes(vs.id) && <Check size={14} className="dbt-voice-check" />}
                                    </div>
                                ))}
                            </div>
                            {selectedPool.length > 0 && (
                                <div className="dbt-selected-count">
                                    {selectedPool.length} voice set{selectedPool.length > 1 ? 's' : ''} selected
                                </div>
                            )}
                            <div className="dbt-modal-notice">
                                <AlertCircle size={14} />
                                <span>Changes take effect after restarting the VM</span>
                            </div>
                        </>
                    )}
                </div>
                <div className="dbt-modal-footer">
                    <button className="dbt-modal-btn secondary" onClick={onClose}>Cancel</button>
                    <button className="dbt-modal-btn primary" onClick={handleSave} disabled={saving || !bot.license_id}>
                        {saving ? <><RefreshCw size={14} className="spinning" /> Saving...</> : <><Check size={14} /> Save Changes</>}
                    </button>
                </div>
            </div>
        </div>
    )
}

// Delete Bot Dialog Component - Password Protected (v3.98)
function DeleteBotDialog({ dialog, onClose, onConfirm }) {
    const [password, setPassword] = useState('')
    const [deleteCallRecords, setDeleteCallRecords] = useState(false)
    const [deleteVMs, setDeleteVMs] = useState(false)
    const [error, setError] = useState('')

    const DELETE_PASSWORD = '474747'
    const isSingle = dialog.type === 'single'
    const botCount = isSingle ? 1 : dialog.bots?.length || 0

    const handleConfirm = () => {
        if (password !== DELETE_PASSWORD) {
            setError('Incorrect password')
            return
        }
        onConfirm(deleteCallRecords, deleteVMs)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && password === DELETE_PASSWORD) {
            handleConfirm()
        }
    }

    return (
        <div className="dbt-modal-overlay" onClick={onClose}>
            <div className="dbt-modal dbt-modal-delete" onClick={e => e.stopPropagation()}>
                <div className="dbt-modal-header dbt-modal-header-danger">
                    <h3><Trash2 size={18} /> Delete {isSingle ? 'Bot' : `${botCount} Bots`}?</h3>
                    <button className="dbt-modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="dbt-modal-body">
                    {isSingle ? (
                        <p className="dbt-delete-warning">
                            You are about to delete <strong>{dialog.bot?.sip_id}</strong> and all associated records (license, machine, bot_status).
                        </p>
                    ) : (
                        <p className="dbt-delete-warning">
                            You are about to delete <strong>{botCount} bots</strong> and all their associated records (licenses, machines, bot_status).
                        </p>
                    )}

                    <label className="dbt-delete-checkbox">
                        <input
                            type="checkbox"
                            checked={deleteVMs}
                            onChange={e => setDeleteVMs(e.target.checked)}
                        />
                        <span>Also delete Proxmox VMs</span>
                    </label>
                    <p className="dbt-delete-hint">
                        {deleteVMs
                            ? '⚠️ VMs will be stopped and permanently deleted from Proxmox'
                            : 'VMs will remain on the Proxmox server (only DB records removed)'}
                    </p>

                    <label className="dbt-delete-checkbox">
                        <input
                            type="checkbox"
                            checked={deleteCallRecords}
                            onChange={e => setDeleteCallRecords(e.target.checked)}
                        />
                        <span>Also delete all call records (call_dispositions)</span>
                    </label>
                    <p className="dbt-delete-hint">
                        {deleteCallRecords
                            ? '⚠️ This will permanently delete all call history for these bots'
                            : 'Call records will be preserved for historical reporting'}
                    </p>

                    <div className="dbt-delete-password">
                        <label>Enter password to confirm deletion</label>
                        <input
                            type="password"
                            value={password}
                            onChange={e => { setPassword(e.target.value); setError(''); }}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter deletion password..."
                            autoFocus
                        />
                        {error && <span className="dbt-delete-error">{error}</span>}
                    </div>
                </div>
                <div className="dbt-modal-footer">
                    <button className="dbt-modal-btn secondary" onClick={onClose}>Cancel</button>
                    <button
                        className="dbt-modal-btn danger"
                        onClick={handleConfirm}
                        disabled={password !== DELETE_PASSWORD}
                    >
                        <Trash2 size={14} /> Delete {isSingle ? 'Bot' : `${botCount} Bots`}
                    </button>
                </div>
            </div>
        </div>
    )
}
